// frontend/src/pages/ConferenceManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import './ConferenceManagement.css';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import { UserContext } from '../contexts/UserContext';

const ConferenceManagement = () => {
  // Temporary: hard-code timezone until the backend / UI supports a proper selector
  const HARD_CODED_TIMEZONE = 'Europe/Moscow';
  const SHOW_SEND_FOR_MODERATION = false;
  const [festschriftForm, setFestschriftForm] = useState(null);
  const [festschriftBaseline, setFestschriftBaseline] = useState(null);
  // now we manage a festschrift, not a conference
  const { festschrift_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrentLanguage, getLocalized } = useLocale();
  const lang = getCurrentLanguage();
  const [managementOptions, setManagementOptions] = useState(null);
  // Prefer slug once we have it (ACR)
  const festIdent = managementOptions?.festschrift?.slug || festschrift_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [saving, setSaving] = useState(false);
  const [newSection, setNewSection] = useState({
    title_ru: '',
    title_en: '',
    slug: '',
  });
  // Track user interaction (show required warnings only after blur)
  const [festTouched, setFestTouched] = useState({});
  // Snapshot of the last loaded/saved form (for "unsaved changes" detection)
  const [lastSavedFestschriftForm, setLastSavedFestschriftForm] = useState(null);
  // In published state we show a compact summary by default, and reveal the full form only on demand.
  const [festEditOpen, setFestEditOpen] = useState(true);
  const festStatus = managementOptions?.festschrift?.status;
  const isDraft = festStatus === 'draft';
  const isPublished = festStatus === 'published';

  useEffect(() => {
    // Draft: keep current behavior (form visible).
    // Published/other: hide the full form by default (can be opened via "Edit festschrift").
    setFestEditOpen(isDraft);
  }, [isDraft]);

  // Publish confirmation modal
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishConfirmBusy, setPublishConfirmBusy] = useState(false);

  // Review template status (for "publish with default template" confirmation)
  const [publishTemplateStatus, setPublishTemplateStatus] = useState(null);

  const needsTemplateConfirm = useMemo(() => {
    // If we couldn't load status yet: require confirmation.
    if (!publishTemplateStatus) return true;
    return !publishTemplateStatus.exists || publishTemplateStatus.is_default;
  }, [publishTemplateStatus]);

  const [sectionTouched, setSectionTouched] = useState({});
  const [creatingSection, setCreatingSection] = useState(false);
  const [deletingSectionId, setDeletingSectionId] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [materialsError, setMaterialsError] = useState('');
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [materialEditing, setMaterialEditing] = useState(null);
  const [materialSaving, setMaterialSaving] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title_ru: '',
    title_en: '',
    comment_ru: '',
    comment_en: '',
    file: null,
  });

  const materialModalRef = useRef(null);
  const materialFileInputRef = useRef(null);
  const [materialDragOver, setMaterialDragOver] = useState(false);

  // Fine-grained permissions from backend (with sensible fallbacks for older API versions)
  const canEditCommon = !!managementOptions?.can_edit;
  const canEditAcronym = managementOptions?.can_edit_acronym ?? isDraft;
  const canChangeReviewFormat = managementOptions?.can_change_review_format ?? isDraft;
  const canEditMaterials = managementOptions?.can_edit_materials ?? canEditCommon;
  const canCreateSections =
    managementOptions?.can_create_sections ?? managementOptions?.can_edit_sections ?? false;
  const canDeleteSections = managementOptions?.can_delete_sections ?? isDraft;

  // --- Editors management (chief_editor can assign/remove editors) ---
  const { user } = useContext(UserContext);

  const [editorsInfo, setEditorsInfo] = useState({ chief_editor: null, editors: [] });
  const [editorsLoading, setEditorsLoading] = useState(false);
  const [editorsError, setEditorsError] = useState('');

  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editorCandidates, setEditorCandidates] = useState([]);
  const [editorSearch, setEditorSearch] = useState('');
  const [editorCandidatesLoading, setEditorCandidatesLoading] = useState(false);
  const [editorCandidatesError, setEditorCandidatesError] = useState('');
  const [editorActionBusyUserId, setEditorActionBusyUserId] = useState(null);
  // Prevent out-of-order search responses from flashing/reverting the list
  const editorCandidatesReqSeq = useRef(0);

  const formatPersonName = (fullNameObj) => {
    if (!fullNameObj) return '';
    // Backend returns { name_ru, name_en }
    if (typeof fullNameObj === 'object' && ('name_ru' in fullNameObj || 'name_en' in fullNameObj)) {
      return lang === 'ru'
        ? fullNameObj.name_ru || fullNameObj.name_en || ''
        : fullNameObj.name_en || fullNameObj.name_ru || '';
    }
    // Fallback for other localized shapes used in the app ({ru,en}, etc.)
    try {
      return getLocalized(fullNameObj);
    } catch {
      return '';
    }
  };

  // determine whether current user is chief_editor by comparing email with returned list
  const isChiefEditor = useMemo(() => {
    const email = (user?.email || '').toLowerCase();
    if (!email) return false;

    const chiefEmail = (editorsInfo?.chief_editor?.email || '').toLowerCase();
    if (chiefEmail && chiefEmail === email) return true;

    // defensive fallback if backend returns chief inside editors list
    return (editorsInfo?.editors || []).some((e) => {
      const eEmail = (e?.email || '').toLowerCase();
      return eEmail === email && e?.role === 'chief_editor';
    });
  }, [user?.email, editorsInfo]);

  const loadEditors = useCallback(async () => {
    if (!festIdent) return;

    setEditorsLoading(true);
    setEditorsError('');

    try {
      const res = await api.get(`/management/publishing/${festIdent}/editors`);
      const list = Array.isArray(res.data) ? res.data : [];

      const chief = list.find((x) => x?.role === 'chief_editor') || null;
      const editors = list.filter((x) => x?.role === 'editor');

      setEditorsInfo({
        chief_editor: chief,
        editors,
      });
    } catch (e) {
      console.error(e);
      setEditorsInfo({ chief_editor: null, editors: [] });
      setEditorsError(lang === 'ru' ? 'Не удалось загрузить редакторов' : 'Failed to load editors');
    } finally {
      setEditorsLoading(false);
    }
  }, [festIdent, lang]);

  const loadEditorCandidates = useCallback(
    async (q) => {
      if (!festIdent) return;

      const reqId = ++editorCandidatesReqSeq.current;

      setEditorCandidatesLoading(true);
      // Keep previous results visible while loading to avoid flicker.
      setEditorCandidatesError('');

      try {
        const res = await api.get(`/management/publishing/${festIdent}/editors/candidates`, {
          params: { query: (q || '').trim() },
        });

        // If a newer request started after this one, ignore this response.
        if (editorCandidatesReqSeq.current !== reqId) return;

        setEditorCandidates(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);

        if (editorCandidatesReqSeq.current !== reqId) return;

        setEditorCandidatesError(
          e?.response?.status === 403
            ? lang === 'ru'
              ? 'Только главный редактор может назначать редакторов'
              : 'Only chief editor can assign editors'
            : lang === 'ru'
              ? 'Не удалось загрузить кандидатов'
              : 'Failed to load candidates',
        );
        // Do NOT clear editorCandidates here; keep previous list to avoid flashing.
      } finally {
        if (editorCandidatesReqSeq.current === reqId) {
          setEditorCandidatesLoading(false);
        }
      }
    },
    [festIdent, lang],
  );

  useEffect(() => {
    if (!editorModalOpen) return;

    const t = setTimeout(() => {
      loadEditorCandidates(editorSearch);
    }, 300);

    return () => clearTimeout(t);
  }, [editorSearch, editorModalOpen, loadEditorCandidates]);

  const openEditorModal = async () => {
    setEditorSearch('');
    setEditorCandidates([]);
    setEditorCandidatesError('');
    setEditorModalOpen(true);
    await loadEditorCandidates('');
  };

  const closeEditorModal = () => {
    setEditorModalOpen(false);
  };

  const assignEditor = async (candidateUserId) => {
    if (!festIdent) return;
    setEditorActionBusyUserId(candidateUserId);
    try {
      await api.post(`/management/publishing/${festIdent}/editors/${candidateUserId}`);
      await loadEditors();
      await loadEditorCandidates(editorSearch);
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;

      if (status === 409) {
        alert(
          lang === 'ru'
            ? 'В формате double-blind пользователь не может быть одновременно рецензентом и редактором'
            : 'In double-blind review a user cannot be both reviewer and editor',
        );
      } else {
        alert(lang === 'ru' ? 'Не удалось назначить редактора' : 'Failed to assign editor');
      }
    } finally {
      setEditorActionBusyUserId(null);
    }
  };

  const removeEditor = async (editorUserId) => {
    if (!festIdent) return;
    const ok = window.confirm(lang === 'ru' ? 'Удалить редактора?' : 'Remove editor?');
    if (!ok) return;

    setEditorActionBusyUserId(editorUserId);
    try {
      await api.delete(`/management/publishing/${festIdent}/editors/${editorUserId}`);
      await loadEditors();
    } catch (e) {
      console.error(e);
      alert(lang === 'ru' ? 'Не удалось удалить редактора' : 'Failed to remove editor');
    } finally {
      setEditorActionBusyUserId(null);
    }
  };

  const filteredEditorCandidates = useMemo(() => {
    const q = (editorSearch || '').trim().toLowerCase();
    if (!q) return editorCandidates || [];

    return (editorCandidates || []).filter((c) => {
      const email = (c?.email || '').toLowerCase();

      // Backend full_name: { name_ru, name_en }
      const fn = c?.full_name;
      const name =
        fn && typeof fn === 'object' && ('name_ru' in fn || 'name_en' in fn)
          ? lang === 'ru'
            ? fn.name_ru || fn.name_en || ''
            : fn.name_en || fn.name_ru || ''
          : typeof fn === 'string'
            ? fn
            : '';

      const fullName = (name || '').toLowerCase();

      return email.includes(q) || fullName.includes(q);
    });
  }, [editorCandidates, editorSearch, lang]);

  const visibleEditorCandidates = useMemo(() => {
    const assigned = new Set([
      ...(editorsInfo?.editors || []).map((x) => x.user_id),
      ...(editorsInfo?.chief_editor ? [editorsInfo.chief_editor.user_id] : []),
    ]);

    return (filteredEditorCandidates || []).filter((c) => !assigned.has(c.user_id));
  }, [filteredEditorCandidates, editorsInfo]);

  // Localized text
  const localizedText = {
    conferenceManagement: {
      ru: 'Управление сборником',
      en: 'Festschrift management',
    },
    changeReviewForm: {
      ru: 'Изменение формы ревью',
      en: 'Change review form',
    },
    availableSections: {
      ru: 'Разделы и статьи',
      en: 'Sections and articles',
    },
    errorLoadingOptions: {
      ru: 'Ошибка загрузки параметров управления',
      en: 'Error loading management options',
    },
    loadingOptions: {
      ru: 'Загрузка параметров управления...',
      en: 'Loading management options...',
    },
    noSectionsAvailable: {
      ru: 'Нет доступных секций для управления',
      en: 'No sections available for management',
    },
    reviewFormatLabel: {
      ru: 'Формат рецензирования',
      en: 'Review format',
    },
    siteLabel: {
      ru: 'Сайт',
      en: 'Site',
    },
    timezoneLabel: {
      ru: 'Часовой пояс',
      en: 'Timezone',
    },

    // Validation / warnings
    validationFixErrors: {
      ru: 'Пожалуйста, исправьте ошибки в форме, чтобы продолжить.',
      en: 'Please fix the invalid fields to continue.',
    },
    validationRequiredField: {
      ru: 'Поле обязательно.',
      en: 'This field is required.',
    },
    validationTitleRequired: {
      ru: 'Укажите название хотя бы на одном языке (RU или EN).',
      en: 'Provide a title in at least one language (RU or EN).',
    },
    validationSlugRequired: {
      ru: 'Акроним обязателен.',
      en: 'Acronym is required.',
    },
    validationSlugFormat: {
      ru: 'Акроним не должен содержать пробелов. Разрешены только латинские буквы, цифры, дефис и подчёркивание.',
      en: 'Acronym must not contain spaces. Only Latin letters, digits, hyphens, and underscores are allowed.',
    },
    validationAcronymLockedAfterPublish: {
      ru: 'После публикации акроним изменить нельзя.',
      en: 'Acronym cannot be changed after publishing.',
    },
    validationReviewFormatLockedAfterPublish: {
      ru: 'После публикации формат рецензирования изменить нельзя.',
      en: 'Review format cannot be changed after publishing.',
    },
    validationDeadlineIncreaseOnly: {
      ru: 'После публикации это значение можно только увеличить.',
      en: 'After publishing this value can only be increased.',
    },
    validationEmailFormat: {
      ru: 'Введите корректный email (например, name@example.com).',
      en: 'Enter a valid email (e.g., name@example.com).',
    },
    validationWebsiteFormat: {
      ru: 'Введите корректный адрес сайта (например, https://example.com).',
      en: 'Enter a valid website URL (e.g., https://example.com).',
    },
    validationPhoneFormat: {
      ru: 'Телефон должен содержать минимум 7 цифр.',
      en: 'Phone number must contain at least 7 digits.',
    },
    validationDateOrderStartEnd: {
      ru: 'Дата начала должна быть не позже даты окончания.',
      en: 'Start date must be on or before the end date.',
    },
    validationDateOrderEndPublication: {
      ru: 'Дата окончания должна быть не позже даты публикации.',
      en: 'End date must be on or before the publication date.',
    },
    validationDateOrderStartPublication: {
      ru: 'Дата начала должна быть не позже даты публикации.',
      en: 'Start date must be on or before the publication date.',
    },
    validationMinReviewers: {
      ru: 'Минимум рецензентов должен быть целым числом не меньше 1.',
      en: 'Min reviewers must be an integer greater than or equal to 1.',
    },
    validationNonNegativeInteger: {
      ru: 'Значение должно быть целым числом не меньше 0.',
      en: 'Value must be an integer greater than or equal to 0.',
    },
    timezoneHardcodedNote: {
      ru: 'Пока что часовой пояс фиксирован: Europe/Moscow.',
      en: 'Timezone is currently fixed to: Europe/Moscow.',
    },
  };

  const reviewFormatOptions = [
    {
      value: 'open',
      label: lang === 'ru' ? 'Открытое (все всех видят)' : 'Open (everyone visible)',
    },
    {
      value: 'double_blind',
      label: lang === 'ru' ? 'Двойное слепое' : 'Double blind',
    },
    {
      value: 'single_blind_for_author',
      label: lang === 'ru' ? 'Автор не видит рецензентов' : "Author can't see reviewers",
    },
  ];

  const reviewFormatDescriptions = {
    open: {
      ru: 'Автор и рецензенты видят имена друг друга.',
      en: 'Author and reviewers see each other’s names.',
    },
    double_blind: {
      ru: 'Ни автор, ни рецензенты не видят имён друг друга.',
      en: 'Neither author nor reviewers see each other’s names.',
    },
    single_blind_for_author: {
      ru: 'Автор не видит имён рецензентов, рецензенты видят автора.',
      en: 'Author does not see reviewers; reviewers see the author.',
    },
  };

  const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

  const statusLabels = {
    draft: { ru: 'Черновик', en: 'Draft' },
    moderation: { ru: 'Модерация', en: 'Moderation' },
    published: { ru: 'Опубликован', en: 'Published' },
    archived: { ru: 'Архив', en: 'Archived' },
  };

  const getStatusText = (rawStatus) => {
    const key = normalizeKey(rawStatus);
    return statusLabels[key]?.[lang] || rawStatus || '-';
  };

  // -------------------------
  // Validation helpers (warnings only; we never modify user input)
  // -------------------------
  const isBlank = (v) => (v === null || v === undefined ? true : String(v).trim() === '');

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const SLUG_RE = /^[A-Za-z0-9_-]+$/; // no spaces, allows hyphen and underscore

  const isValidEmail = (email) => {
    if (isBlank(email)) return true;
    return EMAIL_RE.test(String(email).trim());
  };

  const isValidSlug = (slug) => {
    if (isBlank(slug)) return false;
    const s = String(slug);
    if (/\s/.test(s)) return false;
    return SLUG_RE.test(s);
  };

  const isValidWebsite = (site) => {
    if (isBlank(site)) return true;
    const raw = String(site).trim();
    if (/\s/.test(raw)) return false;

    // Accept either full URLs (with protocol) or domains. We do not mutate user input;
    // the added protocol is only for validation.
    const candidate = raw.includes('://') ? raw : `https://${raw}`;
    try {
      const u = new URL(candidate);
      if (!u.hostname) return false;
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
      return true;
    } catch {
      return false;
    }
  };

  const isValidPhone = (phone) => {
    if (isBlank(phone)) return true;
    const digits = String(phone).replace(/\D/g, '');
    return digits.length >= 7;
  };

  const parseDate = (isoDate) => {
    if (isBlank(isoDate)) return null;
    const d = new Date(isoDate);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const addWarning = (warnings, field, text) => {
    if (!text) return;
    if (!warnings[field]) warnings[field] = text;
    else warnings[field] = `${warnings[field]}\n${text}`;
  };

  const renderWarning = (text) => {
    if (!text) return null;
    const lines = String(text).split('\n');
    return (
      <div className="field-warning" role="alert">
        {lines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}
      </div>
    );
  };

  const requiredAsterisk = <span className="required-asterisk">*</span>;

  const markFestTouched = (field) => {
    setFestTouched((prev) => ({ ...(prev || {}), [field]: true }));
  };

  const markSectionTouched = (field) => {
    setSectionTouched((prev) => ({ ...(prev || {}), [field]: true }));
  };

  const REQUIRED_FEST_FIELDS = new Set(['title_ru', 'title_en', 'slug']);
  const REQUIRED_SECTION_FIELDS = new Set(['title_ru', 'title_en', 'slug']);

  const shouldShowFestWarning = (field) => {
    if (!festschriftWarnings?.[field]) return false;
    if (REQUIRED_FEST_FIELDS.has(field)) return Boolean(festTouched?.[field]);
    return true; // non-required validations can show immediately
  };

  const shouldShowSectionWarning = (field) => {
    if (!newSectionWarnings?.[field]) return false;
    if (REQUIRED_SECTION_FIELDS.has(field)) return Boolean(sectionTouched?.[field]);
    return true;
  };

  const festschriftWarnings = useMemo(() => {
    if (!festschriftForm) return {};

    const warnings = {};
    const t = (key) => getLocalized(localizedText[key]);

    // Titles: both languages are required
    if (isBlank(festschriftForm.title_ru)) {
      addWarning(warnings, 'title_ru', t('validationRequiredField'));
    }
    if (isBlank(festschriftForm.title_en)) {
      addWarning(warnings, 'title_en', t('validationRequiredField'));
    }
    // Subject is required
    if (!Number.isInteger(festschriftForm.subject_id) || festschriftForm.subject_id <= 0) {
      addWarning(warnings, 'subject_id', t('validationRequiredField'));
    }

    // Slug / acronym
    if (isBlank(festschriftForm.slug)) {
      addWarning(warnings, 'slug', t('validationSlugRequired'));
    } else if (!isValidSlug(festschriftForm.slug)) {
      addWarning(warnings, 'slug', t('validationSlugFormat'));
    }

    // Contact fields
    if (!isValidEmail(festschriftForm.email)) {
      addWarning(warnings, 'email', t('validationEmailFormat'));
    }
    if (!isValidWebsite(festschriftForm.site)) {
      addWarning(warnings, 'site', t('validationWebsiteFormat'));
    }
    if (!isValidPhone(festschriftForm.phone)) {
      addWarning(warnings, 'phone', t('validationPhoneFormat'));
    }

    // Dates consistency: start <= end <= publication
    const start = parseDate(festschriftForm.start_date);
    const end = parseDate(festschriftForm.end_date);
    const pub = parseDate(festschriftForm.publication_date);

    if (start && end && start > end) {
      addWarning(warnings, 'start_date', t('validationDateOrderStartEnd'));
      addWarning(warnings, 'end_date', t('validationDateOrderStartEnd'));
    }
    if (end && pub && end > pub) {
      addWarning(warnings, 'end_date', t('validationDateOrderEndPublication'));
      addWarning(warnings, 'publication_date', t('validationDateOrderEndPublication'));
    }
    if (start && pub && start > pub) {
      addWarning(warnings, 'start_date', t('validationDateOrderStartPublication'));
      addWarning(warnings, 'publication_date', t('validationDateOrderStartPublication'));
    }

    // Numbers
    if (
      !Number.isInteger(festschriftForm.min_reviewers_number) ||
      festschriftForm.min_reviewers_number < 1
    ) {
      addWarning(warnings, 'min_reviewers_number', t('validationMinReviewers'));
    }

    const nonNegativeIntegerFields = ['review_deadline', 'correction_deadline'];
    nonNegativeIntegerFields.forEach((field) => {
      const v = festschriftForm[field];
      if (!Number.isInteger(v) || v < 0) {
        addWarning(warnings, field, t('validationNonNegativeInteger'));
      }
    });

    // Timezone is fixed
    // (No warning; we enforce the fixed value on save and on initial load.)
    // Post-publication restrictions enforced both in UI and backend.
    if (isPublished && festschriftBaseline) {
      if (festschriftForm.slug !== festschriftBaseline.slug) {
        addWarning(warnings, 'slug', t('validationAcronymLockedAfterPublish'));
      }
      if (festschriftForm.review_mode !== festschriftBaseline.review_mode) {
        addWarning(warnings, 'review_mode', t('validationReviewFormatLockedAfterPublish'));
      }
      if (
        Number.isInteger(festschriftForm.review_deadline) &&
        festschriftForm.review_deadline < festschriftBaseline.review_deadline
      ) {
        addWarning(warnings, 'review_deadline', t('validationDeadlineIncreaseOnly'));
      }
      if (
        Number.isInteger(festschriftForm.correction_deadline) &&
        festschriftForm.correction_deadline < festschriftBaseline.correction_deadline
      ) {
        addWarning(warnings, 'correction_deadline', t('validationDeadlineIncreaseOnly'));
      }
    }

    return warnings;
  }, [festschriftForm, festschriftBaseline, isPublished, getLocalized, localizedText]);

  const festschriftIsValid = useMemo(
    () => Object.keys(festschriftWarnings).length === 0,
    [festschriftWarnings],
  );
  const normalizeFestForCompare = useCallback((f) => {
    if (!f) return null;
    return {
      title_ru: f.title_ru ?? '',
      title_en: f.title_en ?? '',
      slug: f.slug ?? '',
      organization_id: f.organization_id ?? '',
      description_ru: f.description_ru ?? '',
      description_en: f.description_en ?? '',
      publisher: f.publisher ?? '',
      site: f.site ?? '',
      phone: f.phone ?? '',
      email: f.email ?? '',
      start_date: f.start_date ?? '',
      end_date: f.end_date ?? '',
      publication_date: f.publication_date ?? '',
      review_mode: f.review_mode ?? 'open',
      review_principle: f.review_principle ?? '',
      min_reviewers_number: Number.isFinite(Number(f.min_reviewers_number))
        ? Number(f.min_reviewers_number)
        : 1,
      review_deadline: Number.isFinite(Number(f.review_deadline)) ? Number(f.review_deadline) : 14,
      correction_deadline: Number.isFinite(Number(f.correction_deadline))
        ? Number(f.correction_deadline)
        : 7,
      subject_id: f.subject_id ?? '',
    };
  }, []);

  const hasUnsavedChangesSinceLastSave = useMemo(() => {
    if (!festschriftForm || !lastSavedFestschriftForm) return false;
    return (
      JSON.stringify(normalizeFestForCompare(festschriftForm)) !==
      JSON.stringify(normalizeFestForCompare(lastSavedFestschriftForm))
    );
  }, [festschriftForm, lastSavedFestschriftForm, normalizeFestForCompare]);

  const changedFestschriftFields = useMemo(() => {
    if (!festschriftForm || !lastSavedFestschriftForm) return new Set();

    const current = normalizeFestForCompare(festschriftForm);
    const saved = normalizeFestForCompare(lastSavedFestschriftForm);

    const changed = new Set();
    Object.keys(current).forEach((key) => {
      if (current[key] !== saved[key]) changed.add(key);
    });
    return changed;
  }, [festschriftForm, lastSavedFestschriftForm, normalizeFestForCompare]);

  const festFieldInputClass = useCallback(
    (field, invalid) => {
      const classes = [];

      const isInvalid = typeof invalid === 'boolean' ? invalid : shouldShowFestWarning(field);

      if (isInvalid) classes.push('is-invalid');
      if (changedFestschriftFields.has(field)) classes.push('is-changed');

      return classes.join(' ');
    },
    [shouldShowFestWarning, changedFestschriftFields],
  );

  const newSectionWarnings = useMemo(() => {
    const warnings = {};
    const t = (key) => getLocalized(localizedText[key]);

    const titleRu = (newSection?.title_ru || '').trim();
    const titleEn = (newSection?.title_en || '').trim();
    const slug = (newSection?.slug || '').trim();

    if (isBlank(titleRu)) addWarning(warnings, 'title_ru', t('validationRequiredField'));
    if (isBlank(titleEn)) addWarning(warnings, 'title_en', t('validationRequiredField'));

    if (isBlank(slug)) {
      addWarning(warnings, 'slug', t('validationRequiredField'));
    } else if (!isValidSlug(slug)) {
      addWarning(warnings, 'slug', t('validationSlugFormat'));
    }

    return warnings;
  }, [newSection, getLocalized, localizedText]);

  const newSectionIsValid = useMemo(
    () => Object.keys(newSectionWarnings).length === 0,
    [newSectionWarnings],
  );

  // Название сборника (festschrift) из query-параметров
  const searchParams = new URLSearchParams(location.search);
  const confNameFromQuery = searchParams.get('conferenceName') || '';

  // Fallback cache (so it works when coming from InfoPage without ?conferenceName=...)
  const festCacheKey = `fest_name_${festIdent}`;
  const cachedConfName = (() => {
    try {
      return sessionStorage.getItem(festCacheKey) || '';
    } catch {
      return '';
    }
  })();

  const [confNameResolved, setConfNameResolved] = useState(
    confNameFromQuery || cachedConfName || '',
  );

  // keep cache in sync with query (if present)
  useEffect(() => {
    if (!confNameFromQuery) return;
    try {
      sessionStorage.setItem(festCacheKey, confNameFromQuery);
    } catch {
      //TODO: add handling
    }
    setConfNameResolved(confNameFromQuery);
  }, [confNameFromQuery, festCacheKey]);

  // Хлебные крошки: только название сборника
  const resetBreadcrumbs = useCallback(() => {
    return confNameResolved ? [{ label: confNameResolved }] : [];
  }, [confNameResolved]);

  useUpdateBreadcrumbs(resetBreadcrumbs);

  const setMaterialFile = (file) => {
    setMaterialForm((prev) => ({ ...prev, file: file || null }));
  };

  const openMaterialFilePicker = () => {
    materialFileInputRef.current?.click?.();
  };

  const handleMaterialFileInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    setMaterialFile(file);
  };

  const handleMaterialDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMaterialDragOver(false);
    const file = e.dataTransfer?.files?.[0] || null;
    if (file) setMaterialFile(file);
  };

  const handleMaterialDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!materialDragOver) setMaterialDragOver(true);
  };

  const handleMaterialDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMaterialDragOver(false);
  };

  useEffect(() => {
    if (!materialModalOpen) return;
    requestAnimationFrame(() => {
      // Ensure the modal is visible even if the page is scrolled.
      materialModalRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      const firstFocusable = materialModalRef.current?.querySelector?.(
        'input, select, textarea, button',
      );
      firstFocusable?.focus?.();
    });
  }, [materialModalOpen]);

  const fetchManagementOptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/management/publishing/${festschrift_id}`);

      const fs = response.data?.festschrift;

      // ACR canonicalization: if opened by numeric ID, replace URL with slug
      const currentIdent = String(festschrift_id || '');
      if (fs?.slug && /^\d+$/.test(currentIdent) && currentIdent !== fs.slug) {
        const qs = location.search || '';
        navigate(`/management/publishing/${fs.slug}${qs}`, { replace: true });
        return;
      }

      setManagementOptions(response.data);

      // Prefer real title from API (so breadcrumbs + next navigations have the right name)
      const apiTitle =
        (lang === 'ru' ? fs?.title_ru : fs?.title_en) || fs?.title_en || fs?.title_ru || '';
      if (apiTitle) {
        try {
          sessionStorage.setItem(festCacheKey, apiTitle);
        } catch {
          //TODO: add handling
        }
        setConfNameResolved(apiTitle);
      }

      try {
        const effectiveFestIdent = fs?.slug || festschrift_id;
        const mats = await api.get(`/management/publishing/${effectiveFestIdent}/materials`);
        setMaterials(mats.data || []);
        setMaterialsError('');
      } catch (e) {
        console.error(e);
        setMaterialsError(
          lang === 'ru' ? 'Не удалось загрузить материалы' : 'Failed to load materials',
        );
      }

      if (response.data?.festschrift) {
        const fs = response.data.festschrift;
        const nextForm = {
          title_ru: fs.title_ru || '',
          title_en: fs.title_en || '',
          slug: fs.slug || '',
          organization_id: fs.organization_id || '',
          description_ru: fs.description_ru || '',
          description_en: fs.description_en || '',
          publisher: fs.publisher || '',
          site: fs.site || '',
          phone: fs.phone || '',
          email: fs.email || '',
          start_date: fs.start_date || '',
          end_date: fs.end_date || '',
          publication_date: fs.publication_date || '',
          timezone: HARD_CODED_TIMEZONE,
          review_mode: fs.review_mode || 'open',
          review_principle: fs.review_principle || '',
          min_reviewers_number: fs.min_reviewers_number ?? 1,
          review_deadline: fs.review_deadline ?? 14,
          correction_deadline: fs.correction_deadline ?? 7,
          subject_id: response.data.festschrift_subject?.subject_id || '',
        };
        setFestschriftBaseline({
          slug: fs.slug || '',
          review_mode: fs.review_mode || 'open',
          review_deadline: fs.review_deadline ?? 14,
          correction_deadline: fs.correction_deadline ?? 7,
        });
        setFestschriftForm(nextForm);
        setLastSavedFestschriftForm(nextForm);
        setFestTouched({});
      } else {
        setFestschriftForm(null);
        setLastSavedFestschriftForm(null);
        setFestschriftBaseline(null);
      }

      setError('');
    } catch (err) {
      setError(getLocalized(localizedText.errorLoadingOptions));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [festschrift_id, getLocalized, lang, location.search, navigate]);

  useEffect(() => {
    fetchManagementOptions();
  }, [fetchManagementOptions]);

  useEffect(() => {
    // Editors list is independent from sections/materials
    if (!managementOptions) return;
    loadEditors();
  }, [managementOptions, loadEditors]);

  // Переход к редактору шаблона ревью для сборника
  const goToTemplateEditor = () => {
    // keep query param name "conferenceName" for compatibility
    navigate(
      `/publishing/${festIdent}/template?conferenceName=${encodeURIComponent(confNameResolved)}`,
    );
  };

  // Переход к странице управления секцией (CombinedSectionManagement)
  const handleSectionClick = (sectionId, sectionName) => {
    navigate(
      `/management/publishing/${festIdent}/sections/${sectionId}/articles` +
        `?conferenceName=${encodeURIComponent(confNameResolved)}` +
        `&sectionName=${encodeURIComponent(sectionName)}`,
    );
  };

  const canEditSections = canCreateSections; // legacy alias used in JSX below

  const handleNewSectionFieldChange = (field, value) => {
    setNewSection((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetNewSectionForm = () => {
    setNewSection({
      title_ru: '',
      title_en: '',
      slug: '',
    });
    setSectionTouched({});
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!canEditSections) return;
    if (!newSectionIsValid) return;

    const titleRu = (newSection.title_ru || '').trim();
    const titleEn = (newSection.title_en || '').trim();
    const slug = (newSection.slug || '').trim();

    // All fields are required (same rule as on the Info Page)
    if (!titleRu || !titleEn || !slug) return;

    // Client-side duplicate check (backend also enforces)
    const slugNorm = slug.trim().toLowerCase();
    const hasDuplicate = (managementOptions?.manageableSections || []).some(
      (s) => (s?.slug || '').trim().toLowerCase() === slugNorm,
    );

    if (hasDuplicate) {
      setError(
        lang === 'ru'
          ? 'Раздел с таким акронимом уже существует в этом сборнике'
          : 'A section with this acronym already exists in this festschrift',
      );
      return;
    }

    setCreatingSection(true);

    try {
      const response = await api.post(`/management/publishing/${festIdent}/sections`, {
        title_ru: titleRu,
        title_en: titleEn,
        slug,
      });

      const createdSection = response.data;

      setManagementOptions((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          manageableSections: [...prev.manageableSections, createdSection],
        };
      });

      resetNewSectionForm();
      setError('');
    } catch (err) {
      console.error(err);

      const detail = err?.response?.data?.detail;

      if (detail === 'section_slug_already_exists') {
        setError(
          lang === 'ru'
            ? 'Раздел с таким акронимом уже существует в этом сборнике'
            : 'A section with this acronym already exists in this festschrift',
        );
      } else if (typeof detail === 'string' && detail.trim()) {
        // fallback: show backend detail when it is readable
        setError(detail);
      } else {
        setError(lang === 'ru' ? 'Не удалось создать раздел' : 'Failed to create section');
      }
    } finally {
      setCreatingSection(false);
    }
  };

  const handleDeleteSection = async (sectionId, sectionName) => {
    if (!canDeleteSections) return;

    const confirmed = window.confirm(
      lang === 'ru' ? `Удалить раздел "${sectionName}"?` : `Delete section "${sectionName}"?`,
    );
    if (!confirmed) return;

    setDeletingSectionId(sectionId);
    try {
      await api.delete(`/management/publishing/${festIdent}/sections/${sectionId}`);

      setManagementOptions((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          manageableSections: prev.manageableSections.filter((s) => (s.slug || s.id) !== sectionId),
        };
      });

      setError('');
    } catch (err) {
      console.error(err);
      setError(lang === 'ru' ? 'Не удалось удалить раздел' : 'Failed to delete section');
    } finally {
      setDeletingSectionId(null);
    }
  };

  // Название секции из I8Name
  const getSectionName = (section) => {
    return section.title[`name_${lang}`] || section.title.name_en;
  };

  const handleFestschriftFieldChange = (field, value) => {
    // We allow any characters in inputs, but some fields are constrained by backend rules.
    // Timezone is fixed for now.
    const nextValue = field === 'timezone' ? HARD_CODED_TIMEZONE : value;
    setFestschriftForm((prev) => ({
      ...(prev || {}),
      [field]: nextValue,
    }));
  };

  const handleSaveFestschrift = async () => {
    if (!festschriftForm) return false;

    // Make subject required and avoid sending subject_id: ""
    if (!Number.isInteger(festschriftForm.subject_id) || festschriftForm.subject_id <= 0) {
      setError(lang === 'ru' ? 'Выберите предмет сборника' : 'Please select a festschrift subject');
      return false;
    }

    if (!festschriftIsValid) return false;

    setSaving(true);
    try {
      const payload = {
        ...festschriftForm,
        timezone: HARD_CODED_TIMEZONE,
      };
      // Avoid sending immutable fields (published: acronym + review format are locked)
      if (!canEditAcronym) delete payload.slug;
      if (!canChangeReviewFormat) delete payload.review_mode;
      await api.patch(`/management/publishing/${festIdent}`, payload);

      // If acronym (slug) changed, update URL immediately so all future calls use the new ident.
      if (canEditAcronym) {
        const newSlug = String(payload.slug || '').trim();
        const currentParam = String(festschrift_id || '');

        if (newSlug && newSlug !== currentParam) {
          const qs = location.search || '';
          navigate(`/management/publishing/${newSlug}${qs}`, { replace: true });
          return true; // fetchManagementOptions will run via useEffect on param change
        }
      }

      await fetchManagementOptions();
      return true;
    } catch (err) {
      console.error(err);
      setError(
        lang === 'ru'
          ? 'Не удалось сохранить настройки сборника'
          : 'Failed to save festschrift settings',
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!festschriftIsValid) return;
    try {
      await api.patch(`/management/publishing/${festIdent}/status`, { status: newStatus });
      await fetchManagementOptions();
    } catch (err) {
      console.error(err);
      setError(
        lang === 'ru'
          ? 'Не удалось изменить статус сборника'
          : 'Failed to change festschrift status',
      );
    }
  };

  const fetchPublishTemplateStatus = async () => {
    try {
      const resp = await api.get(`/templates/publishing/${festIdent}/status`);
      const s = resp?.data || null;
      setPublishTemplateStatus(s);
      return s;
    } catch (e) {
      console.error(e);
      // fallback: treat as missing/default so we still confirm.
      const s = { exists: false, is_default: true };
      setPublishTemplateStatus(s);
      return s;
    }
  };

  const handlePublishClick = async () => {
    if (!festschriftIsValid) return;

    const s = await fetchPublishTemplateStatus();
    const templateNeedsConfirm = !s?.exists || s?.is_default;

    // If no unsaved changes and template is custom -> publish immediately.
    if (!hasUnsavedChangesSinceLastSave && !templateNeedsConfirm) {
      await handleStatusChange('published');
      return;
    }

    // Otherwise show confirmation modal (unsaved changes and/or template default/missing)
    setPublishConfirmOpen(true);
  };

  const ensureDefaultTemplateIfMissing = async () => {
    // If status unknown, assume we may need to create it.
    const exists = publishTemplateStatus?.exists;
    if (exists) return;

    // Idempotent: returns existing template OR creates default.
    await api.post(`/templates/publishing/${festIdent}`);
  };

  const publishWithoutApplyingChanges = async () => {
    setPublishConfirmBusy(true);
    try {
      await ensureDefaultTemplateIfMissing();
      await handleStatusChange('published'); // publishes the last saved server state
      setPublishConfirmOpen(false);
    } finally {
      setPublishConfirmBusy(false);
    }
  };

  const applyChangesAndPublish = async () => {
    setPublishConfirmBusy(true);
    try {
      const ok = await handleSaveFestschrift();
      if (!ok) return; // do not publish if save failed

      await ensureDefaultTemplateIfMissing();
      await handleStatusChange('published');
      setPublishConfirmOpen(false);
    } finally {
      setPublishConfirmBusy(false);
    }
  };

  const handleReviewModeChange = (newMode) => {
    setFestschriftForm((prev) => ({
      ...(prev || {}),
      review_mode: newMode,
    }));
  };

  const openAddMaterial = () => {
    setMaterialEditing(null);
    setMaterialForm({
      title_ru: '',
      title_en: '',
      comment_ru: '',
      comment_en: '',
      file: null,
    });
    setMaterialModalOpen(true);
  };

  const openEditMaterial = (m) => {
    setMaterialEditing(m);
    setMaterialForm({
      title_ru: m?.title?.name_ru || '',
      title_en: m?.title?.name_en || '',
      comment_ru: m?.comment?.name_ru || '',
      comment_en: m?.comment?.name_en || '',
      file: null,
    });
    setMaterialModalOpen(true);
  };

  const closeMaterialModal = () => {
    setMaterialModalOpen(false);
    setMaterialEditing(null);
  };

  const reloadMaterials = async () => {
    const mats = await api.get(`/management/publishing/${festIdent}/materials`);
    setMaterials(mats.data || []);
  };

  const saveMaterial = async (e) => {
    e.preventDefault();
    if (!canEditMaterials) return;

    if (!materialEditing && !materialForm.file) {
      setMaterialsError(lang === 'ru' ? 'Выберите файл' : 'Please choose a file');
      return;
    }

    setMaterialSaving(true);
    setMaterialsError('');
    try {
      const fd = new FormData();
      if (materialForm.file) fd.append('file', materialForm.file);
      fd.append('title_ru', materialForm.title_ru || '');
      fd.append('title_en', materialForm.title_en || '');
      fd.append('comment_ru', materialForm.comment_ru || '');
      fd.append('comment_en', materialForm.comment_en || '');

      if (materialEditing) {
        await api.patch(`/management/publishing/${festIdent}/materials/${materialEditing.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post(`/management/publishing/${festIdent}/materials`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      await reloadMaterials();
      closeMaterialModal();
    } catch (err) {
      console.error(err);
      setMaterialsError(
        lang === 'ru' ? 'Не удалось сохранить материал' : 'Failed to save material',
      );
    } finally {
      setMaterialSaving(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const downloadMaterial = async (m) => {
    try {
      const res = await api.get(`/management/publishing/${festIdent}/materials/${m.id}/file`, {
        responseType: 'blob',
      });

      // Try to read filename from Content-Disposition, fallback to original_filename
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i);
      const filename = decodeURIComponent(
        match?.[1] || match?.[2] || m.original_filename || 'material',
      );

      downloadBlob(new Blob([res.data]), filename);
    } catch (err) {
      console.error(err);
      setMaterialsError(lang === 'ru' ? 'Не удалось скачать файл' : 'Failed to download file');
    }
  };

  const deleteMaterial = async (m) => {
    if (!canEditMaterials) return;
    const ok = window.confirm(lang === 'ru' ? 'Удалить материал?' : 'Delete this material?');
    if (!ok) return;

    try {
      await api.delete(`/management/publishing/${festIdent}/materials/${m.id}`);
      await reloadMaterials();
    } catch (err) {
      console.error(err);
      setMaterialsError(
        lang === 'ru' ? 'Не удалось удалить материал' : 'Failed to delete material',
      );
    }
  };

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading-state">{getLocalized(localizedText.loadingOptions)}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <header className="management-header">
        <h1>{lang === 'ru' ? 'Управление сборником' : 'Festschrift management'}</h1>
        <div className="header-actions">
          {isPublished && (
            <button
              type="button"
              className="btn secondary"
              onClick={() => setFestEditOpen((prev) => !prev)}
            >
              {lang === 'ru'
                ? festEditOpen
                  ? 'Скрыть форму'
                  : 'Редактировать сборник'
                : festEditOpen
                  ? 'Hide form'
                  : 'Edit festschrift'}
            </button>
          )}

          {managementOptions?.can_change_template && (
            <button className="template-edit-btn" onClick={goToTemplateEditor}>
              {getLocalized(localizedText.changeReviewForm)}
            </button>
          )}
        </div>
      </header>

      {festschriftForm && (
        <>
          {/* Published: show a compact summary unless editing is opened */}
          {isPublished && !festEditOpen && (
            <div className="festschrift-settings-card">
              <div className="festschrift-compact">
                <div className="festschrift-compact-title">
                  <strong>
                    {lang === 'ru'
                      ? festschriftForm.title_ru || festschriftForm.title_en
                      : festschriftForm.title_en || festschriftForm.title_ru}
                  </strong>
                </div>

                <div className="festschrift-compact-meta">
                  <div>
                    <span className="muted">{lang === 'ru' ? 'Акроним:' : 'Acronym:'}</span>{' '}
                    <strong>{festschriftForm.slug}</strong>
                  </div>

                  <div>
                    <span className="muted">{lang === 'ru' ? 'Даты:' : 'Dates:'}</span>{' '}
                    <strong>
                      {(festschriftForm.start_date || '—') +
                        ' — ' +
                        (festschriftForm.end_date || '—')}
                    </strong>
                    {festschriftForm.publication_date ? (
                      <>
                        {' '}
                        <span className="muted">
                          {lang === 'ru' ? '(публикация' : '(publication'}:{' '}
                          {festschriftForm.publication_date})
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Draft OR explicit edit-open: show the full form (old behavior) */}
          {(!isPublished || festEditOpen) && (
            <div className="festschrift-settings-card">
              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Название (RU)' : 'Title (RU)'} {requiredAsterisk}
                  </label>
                  <input
                    type="text"
                    value={festschriftForm.title_ru}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('title_ru')}
                    onBlur={() => markFestTouched('title_ru')}
                    onChange={(e) => handleFestschriftFieldChange('title_ru', e.target.value)}
                  />
                  {renderWarning(
                    shouldShowFestWarning('title_ru') ? festschriftWarnings.title_ru : null,
                  )}
                </div>
                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Название (EN)' : 'Title (EN)'} {requiredAsterisk}
                  </label>
                  <input
                    type="text"
                    value={festschriftForm.title_en}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('title_en')}
                    onBlur={() => markFestTouched('title_en')}
                    onChange={(e) => handleFestschriftFieldChange('title_en', e.target.value)}
                  />
                  {renderWarning(
                    shouldShowFestWarning('title_en') ? festschriftWarnings.title_en : null,
                  )}
                </div>
              </div>

              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Акроним' : 'Acronym'} {requiredAsterisk}
                  </label>
                  <input
                    type="text"
                    value={festschriftForm.slug}
                    disabled={!canEditCommon || !canEditAcronym}
                    className={festFieldInputClass('slug')}
                    onBlur={() => markFestTouched('slug')}
                    onChange={(e) => handleFestschriftFieldChange('slug', e.target.value)}
                  />
                  {renderWarning(shouldShowFestWarning('slug') ? festschriftWarnings.slug : null)}
                </div>
                <div className="festschrift-field-group" style={{ gridColumn: '1 / -1' }}>
                  <label>{lang === 'ru' ? 'Описание (RU)' : 'Description (RU)'}</label>
                  <textarea
                    value={festschriftForm.description_ru}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('description_ru')}
                    onBlur={() => markFestTouched('description_ru')}
                    onChange={(e) => handleFestschriftFieldChange('description_ru', e.target.value)}
                    rows={4}
                  />
                  {renderWarning(
                    shouldShowFestWarning('description_ru')
                      ? festschriftWarnings.description_ru
                      : null,
                  )}
                </div>

                <div className="festschrift-field-group" style={{ gridColumn: '1 / -1' }}>
                  <label>{lang === 'ru' ? 'Описание (EN)' : 'Description (EN)'}</label>
                  <textarea
                    value={festschriftForm.description_en}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('description_en')}
                    onBlur={() => markFestTouched('description_en')}
                    onChange={(e) => handleFestschriftFieldChange('description_en', e.target.value)}
                    rows={4}
                  />
                  {renderWarning(
                    shouldShowFestWarning('description_en')
                      ? festschriftWarnings.description_en
                      : null,
                  )}
                </div>

                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Издательство' : 'Publisher'}</label>
                  <input
                    type="text"
                    value={festschriftForm.publisher}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('publisher', false)}
                    onChange={(e) => handleFestschriftFieldChange('publisher', e.target.value)}
                  />
                </div>
              </div>

              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>{getLocalized(localizedText.siteLabel)}</label>
                  <input
                    type="text"
                    value={festschriftForm.site}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('site', Boolean(festschriftWarnings.site))}
                    onChange={(e) => handleFestschriftFieldChange('site', e.target.value)}
                  />
                  {renderWarning(festschriftWarnings.site)}
                </div>
                <div className="festschrift-field-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={festschriftForm.email}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('email', Boolean(festschriftWarnings.email))}
                    onChange={(e) => handleFestschriftFieldChange('email', e.target.value)}
                  />
                  {renderWarning(festschriftWarnings.email)}
                </div>
                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Телефон' : 'Phone'}</label>
                  <input
                    type="text"
                    value={festschriftForm.phone}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass('phone', Boolean(festschriftWarnings.phone))}
                    onChange={(e) => handleFestschriftFieldChange('phone', e.target.value)}
                  />
                  {renderWarning(festschriftWarnings.phone)}
                </div>
              </div>

              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Дата начала' : 'Start date'}</label>
                  <input
                    type="date"
                    value={festschriftForm.start_date || ''}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'start_date',
                      Boolean(festschriftWarnings.start_date),
                    )}
                    onChange={(e) => handleFestschriftFieldChange('start_date', e.target.value)}
                  />
                  {renderWarning(festschriftWarnings.start_date)}
                </div>
                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Дата окончания' : 'End date'}</label>
                  <input
                    type="date"
                    value={festschriftForm.end_date || ''}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'end_date',
                      Boolean(festschriftWarnings.end_date),
                    )}
                    onChange={(e) => handleFestschriftFieldChange('end_date', e.target.value)}
                  />
                  {renderWarning(festschriftWarnings.end_date)}
                </div>
                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Дата публикации' : 'Publication date'}</label>
                  <input
                    type="date"
                    value={festschriftForm.publication_date || ''}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'publication_date',
                      Boolean(festschriftWarnings.publication_date),
                    )}
                    onChange={(e) =>
                      handleFestschriftFieldChange('publication_date', e.target.value)
                    }
                  />
                  {renderWarning(festschriftWarnings.publication_date)}
                </div>
              </div>

              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>{getLocalized(localizedText.timezoneLabel)}</label>
                  <input type="text" value={HARD_CODED_TIMEZONE} disabled={true} />
                  <div className="festschrift-help-text">
                    {getLocalized(localizedText.timezoneHardcodedNote)}
                  </div>
                </div>

                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Формат рецензирования' : 'Review format'}</label>
                  <select
                    value={festschriftForm.review_mode}
                    disabled={!canEditCommon || !canChangeReviewFormat}
                    className={festFieldInputClass('review_mode', false)}
                    onChange={(e) => handleReviewModeChange(e.target.value)}
                  >
                    {reviewFormatOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="festschrift-help-text">
                    {reviewFormatDescriptions[festschriftForm.review_mode]?.[lang]}
                  </div>
                </div>

                <div className="festschrift-field-group">
                  <label>{lang === 'ru' ? 'Минимум рецензентов' : 'Min reviewers'}</label>
                  <input
                    type="number"
                    min={1}
                    value={festschriftForm.min_reviewers_number}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'min_reviewers_number',
                      Boolean(festschriftWarnings.min_reviewers_number),
                    )}
                    onChange={(e) =>
                      handleFestschriftFieldChange(
                        'min_reviewers_number',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                  />
                  {renderWarning(festschriftWarnings.min_reviewers_number)}
                </div>
              </div>

              <div className="festschrift-settings-row">
                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Срок рецензирования (дней)' : 'Review deadline (days)'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={festschriftForm.review_deadline}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'review_deadline',
                      Boolean(festschriftWarnings.review_deadline),
                    )}
                    onChange={(e) =>
                      handleFestschriftFieldChange(
                        'review_deadline',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                  />
                  {renderWarning(festschriftWarnings.review_deadline)}
                </div>
                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Срок исправлений (дней)' : 'Correction deadline (days)'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={festschriftForm.correction_deadline}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'correction_deadline',
                      Boolean(festschriftWarnings.correction_deadline),
                    )}
                    onChange={(e) =>
                      handleFestschriftFieldChange(
                        'correction_deadline',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                  />
                  {renderWarning(festschriftWarnings.correction_deadline)}
                </div>

                <div className="festschrift-field-group">
                  <label>
                    {lang === 'ru' ? 'Предмет сборника' : 'Festschrift subject'} {requiredAsterisk}
                  </label>

                  <select
                    value={festschriftForm.subject_id || ''}
                    disabled={!managementOptions.can_edit}
                    className={festFieldInputClass(
                      'subject_id',
                      Boolean(festschriftWarnings.subject_id),
                    )}
                    onChange={(e) => {
                      const hadSubject =
                        Number.isInteger(festschriftForm.subject_id) &&
                        festschriftForm.subject_id > 0;

                      // If a subject is already chosen, do not allow reverting to "Not set".
                      if (hadSubject && !e.target.value) return;

                      handleFestschriftFieldChange(
                        'subject_id',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      );
                    }}
                  >
                    <option
                      value=""
                      disabled={
                        Number.isInteger(festschriftForm.subject_id) &&
                        festschriftForm.subject_id > 0
                      }
                    >
                      {lang === 'ru' ? 'Не выбрано' : 'Not set'}
                    </option>

                    {managementOptions.available_subjects?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {lang === 'ru' ? s.title_ru : s.title_en}
                      </option>
                    ))}
                  </select>

                  {renderWarning(festschriftWarnings.subject_id)}
                </div>
              </div>

              <div className="festschrift-footer-row">
                <div className="festschrift-status">
                  <span className="festschrift-status-label">
                    {lang === 'ru' ? 'Статус:' : 'Status:'}{' '}
                  </span>
                  {(() => {
                    const raw = managementOptions?.festschrift?.status;
                    const key = normalizeKey(raw);
                    return (
                      <span className={`festschrift-status-badge status-${key}`}>
                        {getStatusText(raw)}
                      </span>
                    );
                  })()}
                  {hasUnsavedChangesSinceLastSave && !saving && (
                    <div className="unsaved-changes-note" role="status" aria-live="polite">
                      <span className="dot" aria-hidden="true" />
                      {lang === 'ru'
                        ? `Обнаружены несохранённые изменения (${changedFestschriftFields.size})`
                        : `Unsaved changes detected (${changedFestschriftFields.size})`}
                    </div>
                  )}
                </div>

                <div className="festschrift-actions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!managementOptions.can_edit || saving || !festschriftIsValid}
                    onClick={handleSaveFestschrift}
                  >
                    {saving
                      ? lang === 'ru'
                        ? 'Сохранение...'
                        : 'Saving...'
                      : lang === 'ru'
                        ? 'Сохранить изменения'
                        : 'Save changes'}
                  </button>

                  {managementOptions.festschrift.status === 'draft' && (
                    <>
                      {SHOW_SEND_FOR_MODERATION && (
                        <button
                          type="button"
                          className="btn secondary"
                          disabled={!festschriftIsValid}
                          onClick={() => handleStatusChange('moderation')}
                        >
                          {lang === 'ru' ? 'Отправить на модерацию' : 'Send to moderation'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn secondary"
                        disabled={!festschriftIsValid || saving}
                        onClick={handlePublishClick}
                      >
                        {lang === 'ru' ? 'Опубликовать' : 'Publish'}
                      </button>
                    </>
                  )}

                  {managementOptions.festschrift.status === 'moderation' && (
                    <>
                      <button
                        type="button"
                        className="btn secondary"
                        disabled={!festschriftIsValid}
                        onClick={() => handleStatusChange('draft')}
                      >
                        {lang === 'ru' ? 'Вернуть в черновик' : 'Back to draft'}
                      </button>
                      <button
                        type="button"
                        className="btn secondary"
                        disabled={!festschriftIsValid}
                        onClick={() => handleStatusChange('published')}
                      >
                        {lang === 'ru' ? 'Опубликовать' : 'Publish'}
                      </button>
                    </>
                  )}

                  {managementOptions.festschrift.status === 'published' && (
                    <button
                      type="button"
                      className="btn secondary"
                      disabled={!festschriftIsValid}
                      onClick={() => handleStatusChange('archived')}
                    >
                      {lang === 'ru' ? 'Отправить в архив' : 'Archive'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div className="management-sections">
        <h2>{getLocalized(localizedText.availableSections)}</h2>

        {managementOptions?.manageableSections?.length > 0 ? (
          <ul className="section-list">
            {managementOptions.manageableSections.map((section) => {
              const sectionDisplayName = getSectionName(section);

              return (
                <li
                  key={section.id}
                  className="section-item"
                  onClick={() => handleSectionClick(section.slug || section.id, sectionDisplayName)}
                >
                  <div className="section-name">{sectionDisplayName}</div>

                  {canDeleteSections && (
                    <button
                      type="button"
                      className="section-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSection(section.slug || section.id, sectionDisplayName);
                      }}
                      disabled={deletingSectionId === section.id}
                    >
                      {deletingSectionId === section.id
                        ? lang === 'ru'
                          ? 'Удаление...'
                          : 'Deleting...'
                        : lang === 'ru'
                          ? 'Удалить'
                          : 'Delete'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="no-sections">{lang === 'ru' ? 'Разделы не найдены' : 'No sections'}</div>
        )}

        {canEditSections && (
          <form className="section-new" onSubmit={handleCreateSection}>
            <div className="section-new-title">
              <h3>{lang === 'ru' ? 'Добавить раздел' : 'Add section'}</h3>
            </div>
            <div className="section-new-fields">
              <div className="section-new-field">
                <label>
                  {lang === 'ru' ? 'Название (RU)' : 'Title (RU)'} {requiredAsterisk}
                </label>
                <input
                  type="text"
                  placeholder={lang === 'ru' ? 'Название (RU)' : 'Title (RU)'}
                  value={newSection.title_ru}
                  className={shouldShowSectionWarning('title_ru') ? 'is-invalid' : ''}
                  onBlur={() => markSectionTouched('title_ru')}
                  onChange={(e) => handleNewSectionFieldChange('title_ru', e.target.value)}
                />
                {renderWarning(
                  shouldShowSectionWarning('title_ru') ? newSectionWarnings.title_ru : null,
                )}
              </div>

              <div className="section-new-field">
                <label>
                  {lang === 'ru' ? 'Название (EN)' : 'Title (EN)'} {requiredAsterisk}
                </label>
                <input
                  type="text"
                  placeholder={lang === 'ru' ? 'Название (EN)' : 'Title (EN)'}
                  value={newSection.title_en}
                  className={shouldShowSectionWarning('title_en') ? 'is-invalid' : ''}
                  onBlur={() => markSectionTouched('title_en')}
                  onChange={(e) => handleNewSectionFieldChange('title_en', e.target.value)}
                />
                {renderWarning(
                  shouldShowSectionWarning('title_en') ? newSectionWarnings.title_en : null,
                )}
              </div>

              <div className="section-new-field">
                <label>
                  {lang === 'ru' ? 'Акроним' : 'Acronym'} {requiredAsterisk}
                </label>
                <input
                  type="text"
                  placeholder={lang === 'ru' ? 'Акроним' : 'Acronym'}
                  value={newSection.slug}
                  className={shouldShowSectionWarning('slug') ? 'is-invalid' : ''}
                  onBlur={() => markSectionTouched('slug')}
                  onChange={(e) => handleNewSectionFieldChange('slug', e.target.value)}
                />
                {renderWarning(shouldShowSectionWarning('slug') ? newSectionWarnings.slug : null)}
              </div>

              <button
                type="submit"
                className="btn secondary section-new-submit"
                disabled={creatingSection || !newSectionIsValid}
              >
                {creatingSection
                  ? lang === 'ru'
                    ? 'Создание...'
                    : 'Creating...'
                  : lang === 'ru'
                    ? 'Создать раздел'
                    : 'Create section'}
              </button>
            </div>
          </form>
        )}
      </div>
      <div className="festschrift-settings-card materials-card" style={{ marginTop: 16 }}>
        <div className="materials-header">
          <h2 className="card-title">
            {lang === 'ru' ? 'Дополнительные материалы' : 'Additional materials'}
          </h2>

          <button
            type="button"
            className="btn secondary materials-add-btn"
            onClick={openAddMaterial}
            disabled={!canEditMaterials}
            title={
              canEditMaterials
                ? ''
                : lang === 'ru'
                  ? 'Изменения запрещены в текущем статусе'
                  : 'Changes are not allowed in the current status'
            }
          >
            {lang === 'ru' ? 'Добавить' : 'Add'}
          </button>
        </div>

        {materialsError && <div className="error-message">{materialsError}</div>}

        {materials?.length ? (
          <ul className="materials-list" style={{ marginTop: 12 }}>
            {materials.map((m) => {
              const title = m?.title?.[`name_${lang}`] || m?.title?.name_en || m.original_filename;
              const comment = m?.comment?.[`name_${lang}`] || m?.comment?.name_en || '';

              return (
                <li key={m.id} className="materials-item">
                  <div className="materials-main">
                    <div className="materials-item-title">{title}</div>
                    {comment ? <div className="materials-item-meta">{comment}</div> : null}
                    {m.original_filename ? (
                      <div className="materials-item-file">{m.original_filename}</div>
                    ) : null}
                  </div>

                  <div className="materials-item-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => downloadMaterial(m)}
                      aria-label={`${lang === 'ru' ? 'Скачать' : 'Download'}: ${m.original_filename}`}
                      title={`${lang === 'ru' ? 'Скачать' : 'Download'}: ${m.original_filename}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          d="M12 3v10m0 0 4-4m-4 4-4-4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      type="button"
                      className="btn secondary"
                      onClick={() => openEditMaterial(m)}
                      disabled={!managementOptions?.can_edit}
                    >
                      {lang === 'ru' ? 'Изменить' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      className="btn secondary"
                      onClick={() => deleteMaterial(m)}
                      disabled={!managementOptions?.can_edit}
                    >
                      {lang === 'ru' ? 'Удалить' : 'Delete'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="materials-empty" style={{ marginTop: 12 }}>
            {lang === 'ru' ? 'Материалов пока нет' : 'No materials yet'}
          </div>
        )}
      </div>

      {publishConfirmOpen && (
        <div
          className="material-modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPublishConfirmOpen(false);
          }}
        >
          <div className="material-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="material-dialog-content">
              <div className="material-dialog-header">
                <h3>
                  {hasUnsavedChangesSinceLastSave
                    ? lang === 'ru'
                      ? 'Обнаружены несохранённые изменения'
                      : 'Unsaved changes detected'
                    : lang === 'ru'
                      ? 'Подтвердите публикацию'
                      : 'Confirm publishing'}
                </h3>

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPublishConfirmOpen(false)}
                  aria-label={lang === 'ru' ? 'Закрыть' : 'Close'}
                  title={lang === 'ru' ? 'Закрыть' : 'Close'}
                  disabled={publishConfirmBusy}
                >
                  ×
                </button>
              </div>

              <div style={{ color: 'var(--text)', fontSize: 14, lineHeight: 1.45 }}>
                {needsTemplateConfirm && (
                  <div style={{ marginBottom: hasUnsavedChangesSinceLastSave ? 10 : 0 }}>
                    {lang === 'ru'
                      ? 'Вы не создали шаблон рецензии. Опубликовать сборник с шаблоном по умолчанию?'
                      : "You haven't created a review template. Do you want to publish the festschrift with the default template?"}
                  </div>
                )}

                {hasUnsavedChangesSinceLastSave && (
                  <div>
                    {lang === 'ru'
                      ? 'Обнаружены изменения после последнего сохранения. Применить их перед публикацией?'
                      : 'Changes since the last save have been detected. Do you want to apply them before publishing?'}
                  </div>
                )}

                {!hasUnsavedChangesSinceLastSave && !needsTemplateConfirm && (
                  <div>
                    {lang === 'ru'
                      ? 'Вы уверены, что хотите опубликовать сборник?'
                      : 'Are you sure you want to publish the festschrift?'}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'flex-end',
                  marginTop: 16,
                  flexWrap: 'wrap',
                }}
              >
                {hasUnsavedChangesSinceLastSave ? (
                  <>
                    <button
                      type="button"
                      className="btn secondary"
                      onClick={publishWithoutApplyingChanges}
                      disabled={publishConfirmBusy}
                    >
                      {lang === 'ru' ? 'Опубликовать без изменений' : 'Publish without changes'}
                    </button>

                    <button
                      type="button"
                      className="btn primary"
                      onClick={applyChangesAndPublish}
                      disabled={publishConfirmBusy}
                    >
                      {publishConfirmBusy
                        ? lang === 'ru'
                          ? 'Публикация...'
                          : 'Publishing...'
                        : lang === 'ru'
                          ? 'Применить и опубликовать'
                          : 'Apply & publish'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={publishWithoutApplyingChanges}
                      disabled={publishConfirmBusy}
                    >
                      {publishConfirmBusy
                        ? lang === 'ru'
                          ? 'Публикация...'
                          : 'Publishing...'
                        : needsTemplateConfirm
                          ? lang === 'ru'
                            ? 'Опубликовать с шаблоном по умолчанию'
                            : 'Publish with default template'
                          : lang === 'ru'
                            ? 'Опубликовать'
                            : 'Publish'}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="btn"
                  onClick={() => setPublishConfirmOpen(false)}
                  disabled={publishConfirmBusy}
                >
                  {lang === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {materialModalOpen && (
        <div
          className="material-modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeMaterialModal();
          }}
        >
          <div
            className="material-modal"
            ref={materialModalRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form onSubmit={saveMaterial} className="material-dialog-content">
              <div className="material-dialog-header">
                <h3>
                  {materialEditing
                    ? lang === 'ru'
                      ? 'Редактировать материал'
                      : 'Edit material'
                    : lang === 'ru'
                      ? 'Добавить материал'
                      : 'Add material'}
                </h3>

                <button
                  type="button"
                  className="icon-btn"
                  onClick={closeMaterialModal}
                  aria-label={lang === 'ru' ? 'Закрыть' : 'Close'}
                  title={lang === 'ru' ? 'Закрыть' : 'Close'}
                >
                  ×
                </button>
              </div>

              <div className="material-form">
                <div
                  className={`material-dropzone ${materialDragOver ? 'is-dragover' : ''} ${materialForm.file ? 'has-file' : ''}`}
                  onDragEnter={handleMaterialDragOver}
                  onDragOver={handleMaterialDragOver}
                  onDragLeave={handleMaterialDragLeave}
                  onDrop={handleMaterialDrop}
                  onClick={openMaterialFilePicker}
                  role="button"
                  tabIndex={0}
                  aria-label={lang === 'ru' ? 'Зона загрузки файла' : 'File upload dropzone'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openMaterialFilePicker();
                    }
                  }}
                  style={{ gridColumn: '1 / -1' }}
                >
                  <div className="material-dropzone-text">
                    {materialForm.file
                      ? `${lang === 'ru' ? 'Выбран файл: ' : 'Selected file: '}${materialForm.file.name}`
                      : lang === 'ru'
                        ? 'Перенесите файл сюда'
                        : 'Drop a file here'}
                  </div>

                  <div className="material-dropzone-subtext">
                    {lang === 'ru'
                      ? 'или нажмите, чтобы выбрать файл'
                      : 'or click to choose a file'}
                  </div>

                  <button
                    type="button"
                    className="material-dropzone-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMaterialFilePicker();
                    }}
                  >
                    {lang === 'ru' ? 'Выбрать файл' : 'Choose file'}
                  </button>

                  <input
                    ref={materialFileInputRef}
                    className="material-file-input-hidden"
                    type="file"
                    onChange={handleMaterialFileInputChange}
                  />

                  <div className="material-dropzone-hint muted">
                    {!materialEditing
                      ? lang === 'ru'
                        ? 'Файл обязателен'
                        : 'File is required'
                      : lang === 'ru'
                        ? 'Если выбрать файл — он будет заменён'
                        : 'If you choose a file, it will replace the current one'}
                  </div>
                </div>

                <div>
                  <label>{lang === 'ru' ? 'Файл' : 'File'}</label>
                  <input
                    type="file"
                    onChange={(e) =>
                      setMaterialForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))
                    }
                  />
                  {!materialEditing ? (
                    <div className="muted" style={{ marginTop: 4 }}>
                      {lang === 'ru' ? 'Файл обязателен' : 'File is required'}
                    </div>
                  ) : (
                    <div className="muted" style={{ marginTop: 4 }}>
                      {lang === 'ru'
                        ? 'Если выбрать файл — он будет заменён'
                        : 'If you choose a file, it will replace the current one'}
                    </div>
                  )}
                </div>

                <div>
                  <label>{lang === 'ru' ? 'Заголовок (RU)' : 'Title (RU)'}</label>
                  <input
                    type="text"
                    value={materialForm.title_ru}
                    onChange={(e) => setMaterialForm((p) => ({ ...p, title_ru: e.target.value }))}
                  />
                </div>

                <div>
                  <label>{lang === 'ru' ? 'Заголовок (EN)' : 'Title (EN)'}</label>
                  <input
                    type="text"
                    value={materialForm.title_en}
                    onChange={(e) => setMaterialForm((p) => ({ ...p, title_en: e.target.value }))}
                  />
                </div>

                <div>
                  <label>{lang === 'ru' ? 'Комментарий (RU)' : 'Comment (RU)'}</label>
                  <textarea
                    rows={3}
                    value={materialForm.comment_ru}
                    onChange={(e) => setMaterialForm((p) => ({ ...p, comment_ru: e.target.value }))}
                  />
                </div>

                <div>
                  <label>{lang === 'ru' ? 'Комментарий (EN)' : 'Comment (EN)'}</label>
                  <textarea
                    rows={3}
                    value={materialForm.comment_en}
                    onChange={(e) => setMaterialForm((p) => ({ ...p, comment_en: e.target.value }))}
                  />
                </div>
              </div>

              <div className="material-form-actions">
                <button type="button" className="btn secondary" onClick={closeMaterialModal}>
                  {lang === 'ru' ? 'Отмена' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={materialSaving || !canEditMaterials}
                >
                  {materialSaving
                    ? lang === 'ru'
                      ? 'Сохранение...'
                      : 'Saving...'
                    : materialEditing
                      ? lang === 'ru'
                        ? 'Сохранить'
                        : 'Save'
                      : lang === 'ru'
                        ? 'Добавить материалы'
                        : 'Add materials'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Editors management UI --- */}
      <div className="festschrift-settings-card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h2 style={{ margin: 0 }}>
            {lang === 'ru' ? 'Редакторы сборника' : 'Festschrift editors'}
          </h2>

          {isChiefEditor && (
            <button type="button" className="btn secondary" onClick={openEditorModal}>
              {lang === 'ru' ? 'Назначить редактора' : 'Assign editor'}
            </button>
          )}
        </div>

        {editorsLoading ? (
          <div className="muted" style={{ marginTop: 8 }}>
            {lang === 'ru' ? 'Загрузка...' : 'Loading...'}
          </div>
        ) : editorsError ? (
          <div className="error-state" style={{ marginTop: 8 }}>
            {editorsError}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 10 }}>
              <div className="muted" style={{ fontWeight: 600 }}>
                {lang === 'ru' ? 'Главный редактор' : 'Chief editor'}
              </div>
              {editorsInfo?.chief_editor ? (
                <div>
                  <div>
                    <strong>{editorsInfo.chief_editor.email}</strong>
                  </div>
                  {!!editorsInfo.chief_editor.full_name && (
                    <div className="muted" style={{ marginTop: 2 }}>
                      {formatPersonName(editorsInfo.chief_editor.full_name)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="muted">{lang === 'ru' ? 'Не назначен' : 'Not assigned'}</div>
              )}
            </div>

            <div>
              <div className="muted" style={{ fontWeight: 600, marginBottom: 6 }}>
                {lang === 'ru' ? 'Редакторы' : 'Editors'}
              </div>

              {(editorsInfo?.editors || []).length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {editorsInfo.editors.map((ed) => (
                    <li
                      key={ed.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '8px 0',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <div>
                        <div>
                          <strong>{ed.email}</strong>
                        </div>
                        {!!ed.full_name && (
                          <div className="muted" style={{ marginTop: 2 }}>
                            {formatPersonName(ed.full_name)}
                          </div>
                        )}
                      </div>

                      {isChiefEditor && (
                        <button
                          type="button"
                          className="btn secondary"
                          onClick={() => removeEditor(ed.user_id)}
                          disabled={editorActionBusyUserId === ed.user_id}
                        >
                          {editorActionBusyUserId === ed.user_id
                            ? lang === 'ru'
                              ? '...'
                              : '...'
                            : lang === 'ru'
                              ? 'Удалить'
                              : 'Remove'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="muted">
                  {lang === 'ru' ? 'Пока нет редакторов' : 'No editors yet'}
                </div>
              )}
            </div>

            {!isChiefEditor && (
              <div className="muted" style={{ marginTop: 10 }}>
                {lang === 'ru'
                  ? 'Редакторы имеют те же права, что и главный редактор, но назначать редакторов может только главный редактор.'
                  : 'Editors have the same permissions as the chief editor, but only the chief editor can assign editors.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editors modal */}
      {/* Editors modal (match CombinedSectionManagement modal UI) */}
      {editorModalOpen && (
        <div className="modal-overlay" onMouseDown={closeEditorModal}>
          <div className="modal-window modal-window-wide" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              {lang === 'ru'
                ? 'Назначить редактора (клик по строке)'
                : 'Assign editor (click a row)'}
            </h3>

            <>
              <input
                type="text"
                className="search-input"
                placeholder={lang === 'ru' ? 'Поиск по email...' : 'Search by email...'}
                value={editorSearch}
                onChange={(e) => setEditorSearch(e.target.value)}
                autoFocus
              />

              {/* Show loading/error without unmounting the input  */}
              <div className="modal-table-container">
                {editorCandidatesError && (
                  <div className="modal-table-banner error-state">{editorCandidatesError}</div>
                )}

                <table className="participants-table">
                  <thead>
                    <tr>
                      <th>{lang === 'ru' ? 'Пользователь' : 'User'}</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEditorCandidates.map((c) => (
                      <tr key={c.user_id} onClick={() => assignEditor(c.user_id)}>
                        <td>{c.full_name ? formatPersonName(c.full_name) : '—'}</td>
                        <td>{c.email}</td>
                      </tr>
                    ))}

                    {!editorCandidatesError &&
                      visibleEditorCandidates.length === 0 &&
                      !editorCandidatesLoading && (
                        <tr>
                          <td colSpan={2}>
                            {lang === 'ru' ? 'Кандидаты не найдены' : 'No candidates found'}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>

                {editorCandidatesLoading && (
                  <div className="modal-table-overlay">
                    <div className="loading-state">
                      {lang === 'ru' ? 'Загрузка...' : 'Loading...'}
                    </div>
                  </div>
                )}
              </div>
            </>

            <div className="modal-buttons">
              <button className="modal-cancel-btn" onClick={closeEditorModal}>
                {lang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceManagement;
